import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { isAdmin } from '../utils/role';

const emptyCompany = {
  name: '', code: '', description: '', industry: '', website: '',
  contactPerson: '', contactEmail: '', contactPhone: '', logo: '', notes: '', status: 'ACTIVE',
  devCfApiUrl: '', devCfOrg: '', devCfSpace: '', devCfUsername: '', devCfPassword: '',
  qasCfApiUrl: '', qasCfOrg: '', qasCfSpace: '', qasCfUsername: '', qasCfPassword: '',
  prdCfApiUrl: '', prdCfOrg: '', prdCfSpace: '', prdCfUsername: '', prdCfPassword: '',
  githubUsername: '', githubToken: ''
};

export default function Companies() {
  const [companies, setCompanies] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...emptyCompany });
  const [editing, setEditing] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyProjects, setCompanyProjects] = useState([]);
  const [envTab, setEnvTab] = useState('dev');

  const load = () => {
    api.get('/companies').then(r => setCompanies(r.data));
    api.get('/projects').then(r => setProjects(r.data));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name) return alert('Company name is required');
    if (editing) {
      await api.put(`/companies/${editing}`, form);
    } else {
      await api.post('/companies', form);
    }
    setShowForm(false);
    setEditing(null);
    setForm({ ...emptyCompany });
    load();
  };

  const edit = (c) => {
    setForm({ ...emptyCompany, ...c, devCfPassword: '', qasCfPassword: '', prdCfPassword: '', githubToken: '' });
    setEditing(c.id); setShowForm(true); setSelectedCompany(null);
  };
  const del = async (id) => {
    if (window.confirm('Delete this company?')) {
      await api.delete(`/companies/${id}`);
      if (selectedCompany?.id === id) setSelectedCompany(null);
      load();
    }
  };

  const selectCompany = async (c) => {
    setSelectedCompany(c);
    setShowForm(false);
    try {
      const { data } = await api.get(`/companies/${c.id}/projects`);
      setCompanyProjects(data);
    } catch { setCompanyProjects([]); }
  };

  const [showPasswords, setShowPasswords] = useState({});

  const renderField = (label, field, type) => (
    <div key={field} style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 4 }}>{label}</label>
      {type === 'textarea' ? (
        <textarea value={form[field] || ''} onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
          rows={3} style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14 }} />
      ) : type === 'password' ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type={showPasswords[field] ? 'text' : 'password'} value={form[field] || ''}
            onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
            style={{ flex: 1 }} />
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#6b7280', cursor: 'pointer', whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={!!showPasswords[field]}
              onChange={e => setShowPasswords(p => ({ ...p, [field]: e.target.checked }))} />
            Show
          </label>
        </div>
      ) : (
        <input type={type || 'text'} value={form[field] || ''} onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))} />
      )}
    </div>
  );

  const projectCount = (companyId) => projects.filter(p => p.companyId === companyId).length;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700 }}>Companies</h1>
          <p style={{ color: '#64748b', fontSize: 14 }}>Manage client companies and their DevOps environments</p>
        </div>
        {isAdmin() && <button className="btn-primary" onClick={() => { setForm({ ...emptyCompany }); setEditing(null); setShowForm(true); setSelectedCompany(null); }}>+ Add Company</button>}
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: 20 }}>
          <h3 style={{ marginBottom: 16, fontWeight: 600 }}>{editing ? 'Edit Company' : 'New Company'}</h3>

          <h4 style={{ fontSize: 13, fontWeight: 600, color: '#6b7280', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>General</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 20px' }}>
            {renderField("Company Name", "name")}
            {renderField("Code", "code")}
            {renderField("Industry", "industry")}
            {renderField("Website", "website")}
            {renderField("Contact Person", "contactPerson")}
            {renderField("Contact Email", "contactEmail", "email")}
            {renderField("Contact Phone", "contactPhone")}
            {renderField("Logo URL", "logo")}
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 4 }}>Status</label>
              <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </select>
            </div>
          </div>
          <div style={{ marginTop: 4 }}>
            {renderField("Description", "description", "textarea")}
          </div>

          <h4 style={{ fontSize: 13, fontWeight: 600, color: '#6b7280', marginBottom: 8, marginTop: 16, textTransform: 'uppercase', letterSpacing: 0.5 }}>Environments (CF Config)</h4>
          <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
            {['dev', 'qas', 'prd'].map(t => (
              <button key={t} onClick={() => setEnvTab(t)} style={{
                padding: '6px 16px', fontSize: 13, fontWeight: 600, borderRadius: 6, border: 'none', cursor: 'pointer',
                background: envTab === t ? (t === 'dev' ? '#3b82f6' : t === 'qas' ? '#f59e0b' : '#059669') : 'var(--gray-100)',
                color: envTab === t ? '#fff' : 'var(--gray-600)', textTransform: 'uppercase'
              }}>{t}</button>
            ))}
          </div>

          {envTab === 'dev' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 20px', padding: 16, background: '#eff6ff', borderRadius: 8 }}>
              {renderField("DEV CF API URL", "devCfApiUrl")}
              {renderField("DEV CF Org", "devCfOrg")}
              {renderField("DEV CF Space", "devCfSpace")}
              {renderField("DEV Admin Username (Email)", "devCfUsername")}
              {renderField("DEV Admin Password", "devCfPassword", "password")}
            </div>
          )}
          {envTab === 'qas' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 20px', padding: 16, background: '#fffbeb', borderRadius: 8 }}>
              {renderField("QAS CF API URL", "qasCfApiUrl")}
              {renderField("QAS CF Org", "qasCfOrg")}
              {renderField("QAS CF Space", "qasCfSpace")}
              {renderField("QAS Admin Username (Email)", "qasCfUsername")}
              {renderField("QAS Admin Password", "qasCfPassword", "password")}
            </div>
          )}
          {envTab === 'prd' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 20px', padding: 16, background: '#ecfdf5', borderRadius: 8 }}>
              {renderField("PRD CF API URL", "prdCfApiUrl")}
              {renderField("PRD CF Org", "prdCfOrg")}
              {renderField("PRD CF Space", "prdCfSpace")}
              {renderField("PRD Admin Username (Email)", "prdCfUsername")}
              {renderField("PRD Admin Password", "prdCfPassword", "password")}
            </div>
          )}

          <h4 style={{ fontSize: 13, fontWeight: 600, color: '#6b7280', marginBottom: 8, marginTop: 16, textTransform: 'uppercase', letterSpacing: 0.5 }}>GitHub Credentials</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 20px', padding: 16, background: '#f0f0f0', borderRadius: 8 }}>
            {renderField("GitHub Username", "githubUsername")}
            {renderField("GitHub Token (PAT)", "githubToken", "password")}
          </div>

          <div style={{ marginTop: 8 }}>
            {renderField("Notes", "notes", "textarea")}
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="btn-primary" onClick={save}>{editing ? 'Update' : 'Create'}</button>
            <button className="btn-ghost" onClick={() => { setShowForm(false); setEditing(null); }}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: selectedCompany ? '1fr 1fr' : '1fr', gap: 20 }}>
        <div className="card">
          <h3 style={{ marginBottom: 16, fontWeight: 600 }}>Companies ({companies.length})</h3>
          {companies.length === 0 ? <p style={{ color: '#9ca3af' }}>No companies yet. Add your first company.</p> : (
            <table>
              <thead><tr><th>Company</th><th>Industry</th><th>Contact</th><th>Projects</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {companies.map(c => (
                  <tr key={c.id} onClick={() => selectCompany(c)}
                    style={{ cursor: 'pointer', background: selectedCompany?.id === c.id ? 'var(--primary-light)' : '' }}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {c.logo && <img src={c.logo} alt="" style={{ width: 28, height: 28, borderRadius: 6, objectFit: 'cover' }} />}
                        <div>
                          <div style={{ fontWeight: 600 }}>{c.name}</div>
                          {c.code && <div style={{ fontSize: 11, color: '#6b7280' }}>{c.code}</div>}
                        </div>
                      </div>
                    </td>
                    <td style={{ fontSize: 13 }}>{c.industry || '-'}</td>
                    <td style={{ fontSize: 13 }}>{c.contactPerson || '-'}</td>
                    <td><span className="badge badge-info">{projectCount(c.id)}</span></td>
                    <td><span className={`badge ${c.status === 'ACTIVE' ? 'badge-success' : 'badge-gray'}`}>{c.status}</span></td>
                    <td>
                      {isAdmin() && (
                        <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
                          <button className="btn-ghost" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => edit(c)}>Edit</button>
                          <button className="btn-danger" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => del(c.id)}>Delete</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {selectedCompany && (
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontWeight: 600 }}>{selectedCompany.name}</h3>
              <button className="btn-ghost" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => setSelectedCompany(null)}>Close</button>
            </div>

            {selectedCompany.description && <p style={{ color: '#6b7280', fontSize: 13, marginBottom: 16 }}>{selectedCompany.description}</p>}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16, fontSize: 13 }}>
              {selectedCompany.website && <div><strong>Website:</strong> <a href={selectedCompany.website} target="_blank" rel="noreferrer">{selectedCompany.website}</a></div>}
              {selectedCompany.contactPerson && <div><strong>Contact:</strong> {selectedCompany.contactPerson}</div>}
              {selectedCompany.contactEmail && <div><strong>Email:</strong> {selectedCompany.contactEmail}</div>}
              {selectedCompany.contactPhone && <div><strong>Phone:</strong> {selectedCompany.contactPhone}</div>}
            </div>

            <h4 style={{ fontSize: 13, fontWeight: 600, color: '#6b7280', marginBottom: 8, textTransform: 'uppercase' }}>Environments</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 16 }}>
              <div style={{ padding: 12, background: '#eff6ff', borderRadius: 8, fontSize: 12 }}>
                <div style={{ fontWeight: 700, color: '#3b82f6', marginBottom: 4 }}>DEV</div>
                {selectedCompany.devCfApiUrl && <div>API: {selectedCompany.devCfApiUrl}</div>}
                {selectedCompany.devCfOrg && <div>Org: {selectedCompany.devCfOrg}</div>}
                {selectedCompany.devCfSpace && <div>Space: {selectedCompany.devCfSpace}</div>}
                {!selectedCompany.devCfApiUrl && !selectedCompany.devCfOrg && <div style={{ color: '#9ca3af' }}>Not configured</div>}
              </div>
              <div style={{ padding: 12, background: '#fffbeb', borderRadius: 8, fontSize: 12 }}>
                <div style={{ fontWeight: 700, color: '#f59e0b', marginBottom: 4 }}>QAS</div>
                {selectedCompany.qasCfApiUrl && <div>API: {selectedCompany.qasCfApiUrl}</div>}
                {selectedCompany.qasCfOrg && <div>Org: {selectedCompany.qasCfOrg}</div>}
                {selectedCompany.qasCfSpace && <div>Space: {selectedCompany.qasCfSpace}</div>}
                {!selectedCompany.qasCfApiUrl && !selectedCompany.qasCfOrg && <div style={{ color: '#9ca3af' }}>Not configured</div>}
              </div>
              <div style={{ padding: 12, background: '#ecfdf5', borderRadius: 8, fontSize: 12 }}>
                <div style={{ fontWeight: 700, color: '#059669', marginBottom: 4 }}>PRD</div>
                {selectedCompany.prdCfApiUrl && <div>API: {selectedCompany.prdCfApiUrl}</div>}
                {selectedCompany.prdCfOrg && <div>Org: {selectedCompany.prdCfOrg}</div>}
                {selectedCompany.prdCfSpace && <div>Space: {selectedCompany.prdCfSpace}</div>}
                {!selectedCompany.prdCfApiUrl && !selectedCompany.prdCfOrg && <div style={{ color: '#9ca3af' }}>Not configured</div>}
              </div>
            </div>

            {selectedCompany.githubUsername && (
              <>
                <h4 style={{ fontSize: 13, fontWeight: 600, color: '#6b7280', marginBottom: 8, textTransform: 'uppercase' }}>GitHub</h4>
                <div style={{ padding: 12, background: '#f0f0f0', borderRadius: 8, fontSize: 12, marginBottom: 16 }}>
                  <div><strong>Username:</strong> {selectedCompany.githubUsername}</div>
                  <div><strong>Token:</strong> {selectedCompany.githubToken ? '••••••••' : 'Not set'}</div>
                </div>
              </>
            )}

            <h4 style={{ fontSize: 13, fontWeight: 600, color: '#6b7280', marginBottom: 8, textTransform: 'uppercase' }}>Projects ({companyProjects.length})</h4>
            {companyProjects.length === 0 ? <p style={{ fontSize: 13, color: '#9ca3af' }}>No projects mapped to this company</p> : (
              <table>
                <thead><tr><th>Name</th><th>Platform</th><th>Stack</th><th>Status</th></tr></thead>
                <tbody>
                  {companyProjects.map(p => (
                    <tr key={p.id}>
                      <td style={{ fontWeight: 600, fontSize: 13 }}>{p.name}</td>
                      <td><span className="badge badge-info">{p.platform}</span></td>
                      <td style={{ fontSize: 13 }}>{p.stack}</td>
                      <td><span className={`badge ${p.status === 'ACTIVE' ? 'badge-success' : 'badge-gray'}`}>{p.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {selectedCompany.notes && (
              <>
                <h4 style={{ fontSize: 13, fontWeight: 600, color: '#6b7280', marginBottom: 4, marginTop: 16, textTransform: 'uppercase' }}>Notes</h4>
                <p style={{ fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap' }}>{selectedCompany.notes}</p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
